import { FaxDocument } from './../models/essette-api';
import { autoinject, transient } from "aurelia-framework";
import { Api } from 'app/shared/lib/api/api';
import { ApiQuery } from 'app/shared/lib/api/api-query';
import { IQueryLoadOptions, IApiQueryResults, IApiActionResults } from 'app/shared/lib/api/api-types';
import * as FM from 'app/fm/models/essette-api';
import { ApiModelIdentifier } from 'app/shared/models/essette-api';

export interface IFaxSearchFilters {
    excludeCanceled?: boolean | null;
    canceledOnly?: boolean | null;
    canceledDateStart?: string | null;
    canceledDateEnd?: string | null;
    checkoutUserId?: string | null;
    includeUnlocked?: boolean | null;
    faxDocumentIds?: Array<ApiModelIdentifier> | null;
}


/**
 * Provides access to API models related to Fax Documents
 */
@transient()
@autoinject()
export class FaxService {

    ////****************** Fields and Properties ************************************////


    ////****************** Functions ************************************************////
    constructor(protected readonly api: Api) { }

    public async get<T>(query: ApiQuery): Promise<IApiQueryResults<T>> {
        return await this.api.get(query) as IApiQueryResults<T>;
    }

    ////****************** Queries **************************************************////
    public createLoadFaxDocumentsQuery(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = [], faxSearchFilters?: IFaxSearchFilters, isTracking: boolean = false): ApiQuery {
        const query = this.api
            .query('fm/faxes')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(faxSearchFilters?.faxDocumentIds?.length) {
            query.filterIdentifierArray("id", faxSearchFilters.faxDocumentIds, "eq");
        }

        if(isTracking) {
            query.excludeTotalRecordsCount();
        }

        return query;
    }

    /**
     * Load all `FM.FaxDocument` models
     * @param options query load options
     * @param includes related models to include
     * @param fields query for specific fields
     * @param faxSearchFilters search filters
     * @param isTracking query for tracking purposes
     */
    public async loadFaxDocuments(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = [], faxSearchFilters?: IFaxSearchFilters, isTracking: boolean = false): Promise<IApiQueryResults<FM.FaxDocument>> {
        const query = this.createLoadFaxDocumentsQuery(options, includes, fields, faxSearchFilters, isTracking);
        return await this.api.get(query);
    }



    public createLoadFaxesByQueueQuery(faxSearchFilters: IFaxSearchFilters, queueId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): ApiQuery {
        const query = this.api
            .query(`fm/faxes/queues/${queueId}/faxes`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        //  Filter for only active documents
        if(faxSearchFilters.excludeCanceled) {
            query.filterDate('metadata.canceled.timestamp', null);
        }
        // Filter for only canceled documents
        else if(faxSearchFilters.canceledOnly) {
            query.filterIsNull('metadata.canceled.timestamp', false);
            // Check if daterange filter is set
            if(faxSearchFilters.canceledDateStart) {
                query.filterDateTimeBetweenDates(
                    'metadata.canceled.timestamp',
                    faxSearchFilters.canceledDateStart,
                    faxSearchFilters.canceledDateEnd || null);
            }
        }

        if(faxSearchFilters.checkoutUserId) {
            if(faxSearchFilters.includeUnlocked) {
                query.filterOr(or => or
                    .filterIdentifier('metadata.locked.byUser.id', faxSearchFilters.checkoutUserId!)
                    .filterIsNull('metadata.locked.byUser.id'));
            } else {
                query.filterIdentifier('metadata.locked.byUser.id', faxSearchFilters?.checkoutUserId)
            }
        }

        if(faxSearchFilters.faxDocumentIds?.length) {
            query.filterIdentifierArray("id", faxSearchFilters.faxDocumentIds, "eq");
        }

        return query;
    }

    /**
    * Load all `FM.FaxDocument` models by Queue ID
    * @param queueId fax document queue id
    * @param options query load options
    * @param includes related models to include
    * @param faxSearchFilters search filters
    * @param fields query for specific fields
    * @param isTracking query for tracking purposes
    */
    public async loadFaxesByQueue(queueId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], faxSearchFilters?: IFaxSearchFilters, fields: string[] = []): Promise<IApiQueryResults<FM.FaxDocument>> {
        const query = this.createLoadFaxesByQueueQuery(faxSearchFilters ?? {}, queueId, options, includes, fields);

        return await this.api.get(query);
    }

    public async loadQueueActionsByQueue(queueId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<FM.FaxQueueAction>> {
        const query = this.api
            .query(`fm/faxes/queues/${queueId}/actions`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }


    ////****************** Loads ****************************************************////
    /**
     * Load `FM.FaxDocument` information by ID
     * @param faxDocumentID fax document ID to lookup
     * @param includes related models to include
     * @param fields query for specific fields
     */
    public async loadFaxDocument(faxDocumentId: string, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<FM.FaxDocument>> {
        const query = this.api
            .query(`fm/faxes/${faxDocumentId}`)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    /**
     * Load all `FM.FaxQueue` models
     * @param options query load options
     * @param includes related models to include
     */
    public async loadFaxQueues(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<FM.FaxQueue>> {
        const query = this.api
            .query('fm/faxes/queues/')
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadQueue(queueId: string, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<FM.FaxQueue>> {
        const query = this.api
            .query(`fm/faxes/queues/${queueId}`)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    /**
     * Load all `FM.FaxQueueAction` models
     * @param options query load options
     * @param includes related models to include
     */
    public async loadFaxQueueActions(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<FM.FaxQueueAction>> {
        const query = this.api
            .query('fm/faxes/queues/actions')
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    /**
    * Load `FM.FaxQueueAction` model based on Fax Queue ID
    * @param queueID Fax Queue ID to look up
    * @param options query load options
    * @param includes related models to include
    */
    public async loadFaxQueueActionsByQueue(queueId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<FM.FaxQueueAction>> {
        const query = this.api
            .query(`fm/faxes/queues/${queueId}/actions`)
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    /**
    * Load all `FM.FaxAccount` models
    * @param options query load options
    * @param includes related models to include
    */
    public async loadFaxAccounts(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<FM.FaxAccount>> {
        const query = this.api
            .query('fm/faxes/accounts/')
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    ////****************** Actions **************************************************////
    /**
     * Inserts or updates an `FM.FaxQueue` model
     * @param faxQueue the fax queue object
     */
    public async saveFaxQueue(faxQueue: FM.FaxQueue): Promise<IApiActionResults> {
        const actionName = !faxQueue.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `fm/faxes/queues/${actionName === 'update' ? faxQueue.id : ''}`, faxQueue);

        return await this.api.post(action);
    }

    /**
     * Inserts or updates an `FM.FaxAccount` model
     * @param faxAccount the fax account object
     */
    public async saveFaxAccount(faxAccount: FM.FaxAccount): Promise<IApiActionResults> {
        const actionName = !faxAccount.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `fm/faxes/accounts/${actionName === 'update' ? faxAccount.id : ''}`, faxAccount);

        return await this.api.post(action);
    }

    /**
     * Deletes actions and parameters from an `FM.FaxQueue` model
     * @param faxQueueId the ID of the fax queue
     */
    public async deleteFaxQueueDetails(faxQueueId: string): Promise<IApiActionResults> {
        const action = this.api.action('deleteDetails', `fm/faxes/queues/${faxQueueId}`, {});

        return await this.api.post(action);
    }

    /**
     * Activates or deactivates an `FM.FaxQueue` model by ID
     * @param faxQueueId the fax queue's ID
     * @param isActive true if activating, false if deactivating
     */
    public async activateFaxQueue(faxQueueId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `fm/faxes/queues/${faxQueueId}`, { isActive });

        return isActive ? await this.api.post(action) : await this.api.delete(action);
    }

    /**
     * Activates or deactivates an `FM.FaxAccount` model by ID
     * @param faxAccountId the fax account's ID
     * @param isActive true if activating, false if deactivating
     */
    public async activateFaxAccount(faxAccountId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `fm/faxes/accounts/${faxAccountId}`, { isActive });

        return isActive ? await this.api.post(action) : await this.api.delete(action);
    }

    public async loadFaxPriorities(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<FM.FaxPriority>> {
        const query = this.api
            .query('/fm/faxes/priorities')
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async checkInFaxDocument(faxDocumentId: string): Promise<IApiActionResults> {

        const action = this.api.action('checkin', `fm/faxes/${faxDocumentId}`, { isLocked: false });
        return await this.api.post(action);
    }


    public async checkOutFaxDocument(faxDocumentId: string): Promise<IApiActionResults> {

        const action = this.api.action('checkout', `fm/faxes/${faxDocumentId}`, { isLocked: true });
        return await this.api.post(action);

    }

    /**
    * Cancels a `FM.FaxDocument` model by ID
    * @param faxDocumentId the fax document's ID
    */
    public async cancelFaxDocument(faxDocumentId: string): Promise<IApiActionResults> {
        const action = this.api.action('cancel', `fm/faxes/${faxDocumentId}`, { isCanceled: true });

        return await this.api.post(action);
    }

    /**
    * Activate/Reactivate a `FM.FaxDocument` model by ID
    * @param faxDocumentId the fax queue's ID
    */
    public async activateFaxDocument(faxDocumentId: string): Promise<IApiActionResults> {
        const action = this.api.action('reactivate', `fm/faxes/${faxDocumentId}`, { isActive: true });

        return await this.api.post(action);
    }

    /**
    * Attach Fax Document
    * @param document the fax document info to attach to the queue
    */
    public async attachFaxDocument(document: FM.FaxDocument): Promise<IApiActionResults> {
        const action = this.api.action('attach', `fm/faxes/`, document);

        return await this.api.post(action);
    }

    /**
    * Route Fax Document
    * @param faxDocumentId the fax queue's ID
    * @param payload the json body to post to the endpoint to determine how to route
    */
    public async routeFaxDocument(faxDocumentId: string, payload: object): Promise<IApiActionResults> {
        const action = this.api.action('route', `fm/faxes/${faxDocumentId}`, payload);

        return await this.api.post(action);
    }

    /**
    * Download Fax Document
    * @param faxDocumentId the fax document id to identify and download fax document
    */
    public async downloadFaxFile(faxDocumentId: string): Promise<Blob> {
        const request = this.api.download(`fm/faxes/${faxDocumentId}`);

        return await this.api.get(request);
    }

    /**
    * Split Fax Document
    * @param document the fax document info to split
    */
    public async splitFaxDocument(document: FaxDocument): Promise<IApiActionResults> {

        const action = this.api.action('SplitAndRoute', `fm/faxes/${document.id}`, document);
        return await this.api.post(action);

    }

    /**
    * Rotate Fax Document
    * @param faxDocumentId the fax document identifier to determine the document to rotate
    * @param payload the json body to post to the endpoint to determine how many degrees to rotate the document
    */
    public async rotateFaxDocument(faxDocumentId: string, payload: object): Promise<IApiActionResults> {
        const action = this.api.action('rotate', `fm/faxes/${faxDocumentId}`, payload);

        return await this.api.post(action);
    }
}
